// src/modals/SettingsPanel.jsx
// Phase 37 Plan 06 — extracted from src/App.jsx (was lines 5007-5235 pre-Plan-37-06).
//
// Scope: settings panel — theme toggle, region picker, Garden Reset section,
//        dev-mode-conditional rendering. The HIGHEST-RISK modal in the split
//        per RESEARCH §G Plan 37-05.
//
// Notes for future phases:
// - DEV_TOOLS keys are wired by App() through the `devActions` prop bag
//   (clearPlants / resetCompletions / resetDismissedTips / loadTestData /
//    viewEvents / clearAnalytics). Adding a new dev action requires both a
//    DEV_TOOLS entry here AND a matching key in App()'s devActions object.
// - Garden Reset is intentionally NOT dev-gated — the typed-DELETE confirmation
//   dialog is the sole safety gate (Phase 31.1 Plan 04, UAT Gap 5).
// - Simulate toggles (Premium / Upgrade prompts / Review prompt / Inspire / Featured
//   Supplier) compose additively in dev mode per Phase 27 Plan 03 Gap 2.
// - DEV_BLUE colour token is shared with AboutPanel; kept as a private const
//   here to avoid a cross-modal import.

import { useState } from 'react';
import { X, Sun, Moon, SunMoon, Info, LayoutGrid, Sprout, Sparkles, Trash2, Cloud, User, LogOut, ListTodo, ChevronRight, Type } from 'lucide-react';
// Phase 40 Plan 03 Task 2 — sync status + dead-letter affordance.
import SyncStatusIndicator from '../home-panel/SyncStatusIndicator.jsx';
import DeadLetterModal from './DeadLetterModal.jsx';
// Phase 40 Plan 04 Task 1 — Account section build-out.
import DeleteAccountModal from './DeleteAccountModal.jsx';
// Phase 40 Plan 04 Task 2 — Activity log Settings sub-page.
import ActivityLogView from '../views/activity-log/ActivityLogView.jsx';
import { readDeadLetter, discardDeadLetter, writeWal, readWal, pushWalOp } from '../lib/storage.js';

const DEV_BLUE = "#1976d2";

export default function SettingsPanel({darkMode,darkModePref,cycleDarkMode,textSize='1',onTextSizeChange=()=>{},devModeAllowed,devModeActive,setDevModeActive,featureTier,simulatePremium,onSimulatePremium,simulateUpgradePrompts,onSimulateUpgradePrompts,simulateReviewPrompt,onSimulateReviewPrompt,simulateInspireMe,onSimulateInspireMe,simulateFeaturedSupplier,onSimulateFeaturedSupplier,onDevSimulateReset,onShowPremium,plants,regionPref,resolvedRegion,onRegionChange,devActions,onShowTour,onManageSpaces,onDeleteAllData,onClose,
  // Phase 40 Plan 03 — auth + sync state plumbed by App.jsx.
  authSession=null, syncStatus={status:'synced',count:0}, deadLetterCount=0, onSignIn=()=>{}, onSignOut=()=>{}, onRefreshDeadLetter=()=>{},
  // Phase 40 Plan 04 — deleteAccount handler from AuthProvider context.
  onDeleteAccount=null,
  // Admin panel dev bypass — sets tend:adminDevBypass and navigates.
  onLaunchAdmin=null}) {
  const [devConfirm,setDevConfirm]=useState(null); // null | "clearPlants" | "resetCompletions" | "resetDismissedTips"
  const [showDeadLetter,setShowDeadLetter]=useState(false);
  const [showDeleteAccount,setShowDeleteAccount]=useState(false);
  const [showActivityLog,setShowActivityLog]=useState(false);
  const [deleting,setDeleting]=useState(false);
  const [deleteError,setDeleteError]=useState(null);
  const [confirmSignOut,setConfirmSignOut]=useState(false);
  const handleTryAgain=(op)=>{
    // Move the op back to WAL so the next drain picks it up.
    try{
      const wal=readWal();
      pushWalOp({op:op.op,table:op.table,cloudId:op.cloudId,payload:op.payload});
      // Drop the dead-letter entry once requeued.
      discardDeadLetter([`${op.ts}|${op.cloudId}`]);
      onRefreshDeadLetter();
    }catch{}
  };
  const handleDiscardOne=(op)=>{
    try{discardDeadLetter([`${op.ts}|${op.cloudId}`]);onRefreshDeadLetter();}catch{}
  };
  const handleTryAll=()=>{
    try{
      for(const op of readDeadLetter()){
        pushWalOp({op:op.op,table:op.table,cloudId:op.cloudId,payload:op.payload});
      }
      writeDeadLetterArray([]);
      onRefreshDeadLetter();
      setShowDeadLetter(false);
    }catch{}
  };
  const handleDiscardAll=()=>{
    try{writeDeadLetterArray([]);onRefreshDeadLetter();setShowDeadLetter(false);}catch{}
  };
  // Local helper — drains the dead-letter list to []. We don't import a
  // dedicated `clearDeadLetter` because the bulk action is the only caller.
  function writeDeadLetterArray(arr){
    try{localStorage.setItem('tend:wal-deadletter',JSON.stringify(arr));}catch{}
  }

  const DEV_TOOLS=[
    {key:"clearPlants",      label:"Clear Plant Data",       desc:`Permanently remove all ${plants.length} plants from Tend`,         danger:true},
    {key:"resetCompletions", label:"Reset Completions",      desc:"Clear all task completion records — tasks will show as outstanding", danger:true},
    {key:"resetDismissedTips",label:"Reset Dismissed Tips",  desc:"Restore all tip cards that were dismissed on the Year view",        danger:false},
    // Phase 31.1 Plan 02 (#208 D-06) — replace current garden with the 45-plant sample dataset.
    // Dev-mode only (entire DEV_TOOLS section is gated by devModeAllowed && devModeActive above).
    {key:"loadTestData",     label:"Load test data (45 plants)", desc:"Replace current data with the sample garden (45 plants, 2 gardens)", danger:false},
    // Phase 32 D-06 — analytics validation panel
    {key:"viewEvents",       label:"View events",            desc:"Show the most recent tend:events entries (newest first) as JSON",   danger:false},
    {key:"clearAnalytics",   label:"Clear analytics",        desc:"Wipe tend:events from this device (does NOT touch tend:eventsSyncCursor)", danger:true},
  ];

  function runDevAction(key) {
    devActions[key]();
    setDevConfirm(null);
  }

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div style={{background:"var(--c-bg)",borderRadius:18,padding:28,maxWidth:480,width:"100%",maxHeight:"90vh",overflowY:"auto",boxShadow:"0 8px 40px rgba(0,0,0,0.2)",position:"relative"}}>
        <button onClick={onClose} style={{position:"absolute",top:16,right:16,background:"none",border:"none",cursor:"pointer",color:"var(--c-text-3)",padding:4,borderRadius:8,lineHeight:1,display:"flex"}} aria-label="Close settings"><X size={20}/></button>
        <h2 style={{fontFamily:"var(--font-family-display)",color:"var(--c-heading)",margin:"0 0 6px",fontSize:"var(--font-h1)"}}>Settings</h2>
        <hr style={{border:"none",borderTop:"1px solid var(--c-border)",margin:"14px 0"}}/>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            {darkModePref==="off"?<Sun size={16} className="t-heading"/>:darkModePref==="on"?<Moon size={16} className="t-heading"/>:<SunMoon size={16} className="t-heading"/>}
            <span className="t-strong-text">Dark mode</span>
          </div>
          <button onClick={cycleDarkMode} style={{background:darkMode?"var(--c-nav-bg)":"var(--c-btn-bg)",color:darkMode?"var(--c-accent-text)":"var(--c-btn-text)",border:"none",borderRadius:20,padding:"6px 16px",fontSize:'var(--font-body)',fontWeight:600,cursor:'pointer',minWidth:56,textAlign:"center"}}>
            {darkModePref==="off"?"Off":darkModePref==="on"?"On":"Auto"}
          </button>
        </div>
        {/* Text size picker (#248) — native <select> matching the Region
            dropdown pattern. Sets --text-scale on <html> via App.jsx; the rem
            migration consumes the variable through the root font-size calc()
            in src/ui.css. */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <Type size={16} className="t-heading"/>
            <span className="t-strong-text">Text size</span>
          </div>
          <select
            value={textSize}
            onChange={(e)=>onTextSizeChange(e.target.value)}
            aria-label="Text size"
            style={{background:"var(--c-btn-bg)",color:"var(--c-btn-text)",border:"1px solid var(--c-border)",borderRadius:20,padding:"6px 14px",fontSize:'var(--font-input)',fontWeight:600,cursor:"pointer",minWidth:140,textAlign:"center",fontFamily:"inherit"}} // UAT2-GAP-07 iOS zoom floor
          >
            <option value="0.875">Smaller</option>
            <option value="1">System default</option>
            <option value="1.125">Larger</option>
            <option value="1.25">Largest</option>
          </select>
        </div>
        {/* Region selector — MO-03 / D-05 / D-06 */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <Info size={16} className="t-heading"/>
            <span className="t-strong-text">Region</span>
          </div>
          <select
            value={regionPref}
            onChange={(e)=>onRegionChange(e.target.value)}
            aria-label="Region"
            style={{background:"var(--c-btn-bg)",color:"var(--c-btn-text)",border:"1px solid var(--c-border)",borderRadius:20,padding:"6px 14px",fontSize:'var(--font-input)',fontWeight:600,cursor:"pointer",minWidth:140,textAlign:"center",fontFamily:"inherit"}} // UAT2-GAP-07 iOS zoom floor
          >
            <option value="auto">{`Auto (${resolvedRegion})`}</option>
            <option value="GB">United Kingdom</option>
            <option value="DE">Germany</option>
            <option value="FR">France</option>
            <option value="NL">Netherlands</option>
          </select>
        </div>
        {/* Phase 31.1 Plan 02 — Garden Setup section (#207 D-07) */}
        <div style={{marginTop:4,paddingTop:18,borderTop:"1px solid var(--c-border)",marginBottom:20}}>
          <h3 style={{fontFamily:"var(--font-family-display)",fontSize:'var(--font-body)',fontWeight:700,color:"var(--c-heading)",margin:"0 0 12px"}}>Garden Setup</h3>
          <div className="row-between">
            <div style={{display:"flex",alignItems:"center",gap:10,flex:1,minWidth:0}}>
              <LayoutGrid size={16} className="t-heading-shrink"/>
              <div>
                <div className="t-strong-text">Manage spaces</div>
                <div className="t-meta">Add, rename, or remove garden areas</div>
              </div>
            </div>
            <button onClick={()=>{onManageSpaces?.();}}
              style={{background:"var(--c-btn-bg)",color:"var(--c-btn-text)",border:"none",borderRadius:20,padding:"6px 16px",fontSize:'var(--font-body)',fontWeight:600,cursor:'pointer',flexShrink:0}}>
              Manage
            </button>
          </div>
        </div>
        {/* Welcome tour re-run — Phase 21 / ONB-01 / D-12 */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
          <div style={{display:"flex",alignItems:"center",gap:10,flex:1,minWidth:0}}>
            <Sprout size={16} className="t-heading-shrink"/>
            <span className="t-strong-text">Welcome tour</span>
          </div>
          <button onClick={onShowTour} style={{background:"var(--c-btn-bg)",color:"var(--c-btn-text)",border:"none",borderRadius:20,padding:"6px 16px",fontSize:'var(--font-body)',fontWeight:600,cursor:'pointer',flexShrink:0}}>
            Show welcome tour
          </button>
        </div>
        {/* Why Go Premium? link — shown when on free tier */}
        {featureTier==='free'&&(
          <div style={{marginTop:18,paddingTop:18,borderTop:"1px solid var(--c-border)"}}>
            <button onClick={onShowPremium} style={{background:"none",border:"none",cursor:"pointer",color:"var(--c-heading)",fontSize:'var(--font-small)',fontWeight:600,padding:0,display:"flex",alignItems:"center",gap:6}}>
              <Sparkles size={14} color="var(--c-heading)"/>
              Why Go Premium?
            </button>
          </div>
        )}
        {/* Developer Mode section — only visible when server flag is set and dev mode is active */}
        {devModeAllowed&&devModeActive&&(
          <>
            <hr style={{border:"none",borderTop:`2px solid ${DEV_BLUE}`,margin:"24px 0 18px"}}/>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16}}>
              <div style={{width:8,height:8,borderRadius:"50%",background:DEV_BLUE,flexShrink:0}}/>
              <span style={{fontFamily:"var(--font-family-display)",fontSize:'var(--font-h3)',fontWeight:700,color:DEV_BLUE,flex:1}}>Developer Mode</span>
              <button onClick={()=>setDevModeActive(false)} style={{background:"none",color:DEV_BLUE,border:`1px solid ${DEV_BLUE}`,borderRadius:20,padding:"5px 14px",fontSize:'var(--font-small)',fontWeight:600,cursor:'pointer'}}>Turn off</button>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {/* Simulate Premium toggle */}
              <div style={{borderRadius:10,border:`1px solid ${simulatePremium?DEV_BLUE:"var(--c-border)"}`,overflow:"hidden"}}>
                <div className="row-between-pad">
                  <div>
                    <div className="t-strong-text">Simulate Premium</div>
                    <div className="t-meta">Override all feature flags to Premium tier</div>
                  </div>
                  <button onClick={()=>onSimulatePremium(v=>!v)}
                    style={{background:simulatePremium?DEV_BLUE:"rgba(25,118,210,0.08)",color:simulatePremium?"#fff":DEV_BLUE,border:"none",borderRadius:20,padding:"6px 14px",fontSize:'var(--font-small)',fontWeight:600,cursor:'pointer',flexShrink:0,marginLeft:12}}>
                    {simulatePremium?"On":"Off"}
                  </button>
                </div>
              </div>
              {/* Simulate upgrade prompts toggle (D-16) */}
              <div style={{borderRadius:10,border:`1px solid ${"var(--c-heading)"==="var(--c-heading)"&&simulateUpgradePrompts?"var(--c-heading)":"var(--c-border)"}`,overflow:"hidden"}}>
                <div className="row-between-pad">
                  <div>
                    <div className="t-strong-text">Simulate upgrade prompts</div>
                    <div className="t-meta">Preview upgrade prompt behaviour with the production flag off.</div>
                  </div>
                  <button onClick={()=>onSimulateUpgradePrompts(prev=>{
                      const next=!prev;
                      if(!prev&&next&&devModeActive&&typeof onDevSimulateReset==='function') onDevSimulateReset('upgrade');
                      return next;
                    })}
                    style={{background:simulateUpgradePrompts?"var(--c-heading)":"rgba(45,90,45,0.08)",color:simulateUpgradePrompts?"#fff":"var(--c-heading)",border:"none",borderRadius:20,padding:"6px 14px",fontSize:'var(--font-small)',fontWeight:600,cursor:'pointer',flexShrink:0,marginLeft:12}}>
                    {simulateUpgradePrompts?"On":"Off"}
                  </button>
                </div>
              </div>
              {/* Phase 27 ENG-03 — Simulate Review Prompt toggle (mirrors simulateUpgradePrompts) */}
              <div style={{borderRadius:10,border:`1px solid ${simulateReviewPrompt?"var(--c-heading)":"var(--c-border)"}`,overflow:"hidden"}}>
                <div className="row-between-pad">
                  <div>
                    <div className="t-strong-text">Simulate Review Prompt</div>
                    <div className="t-meta">Preview the review prompt card without flipping the server flag.</div>
                  </div>
                  <button onClick={()=>onSimulateReviewPrompt(prev=>{
                      const next=!prev;
                      if(!prev&&next&&devModeActive&&typeof onDevSimulateReset==='function') onDevSimulateReset('review');
                      return next;
                    })}
                    style={{background:simulateReviewPrompt?"var(--c-heading)":"rgba(45,90,45,0.08)",color:simulateReviewPrompt?"#fff":"var(--c-heading)",border:"none",borderRadius:20,padding:"6px 14px",fontSize:'var(--font-small)',fontWeight:600,cursor:'pointer',flexShrink:0,marginLeft:12}}>
                    {simulateReviewPrompt?"On":"Off"}
                  </button>
                </div>
              </div>
              {/* Phase 31.1 Plan 05 / UAT Gap 3 — Force-show Inspire Me card toggle */}
              <div style={{borderRadius:10,border:`1px solid ${simulateInspireMe?"var(--c-heading)":"var(--c-border)"}`,overflow:"hidden"}}>
                <div className="row-between-pad">
                  <div>
                    <div className="t-strong-text">Force-show Inspire Me card</div>
                    <div className="t-meta">Bypass dismissed-state and rotation — always shows the top Inspire candidate (UAT helper).</div>
                  </div>
                  <button onClick={()=>onSimulateInspireMe(prev=>!prev)}
                    style={{background:simulateInspireMe?"var(--c-heading)":"rgba(45,90,45,0.08)",color:simulateInspireMe?"#fff":"var(--c-heading)",border:"none",borderRadius:20,padding:"6px 14px",fontSize:'var(--font-small)',fontWeight:600,cursor:'pointer',flexShrink:0,marginLeft:12}}>
                    {simulateInspireMe?"On":"Off"}
                  </button>
                </div>
              </div>
              {/* Phase 31.1 Plan 05 / UAT Gap 3 — Force-show Featured Supplier card toggle */}
              <div style={{borderRadius:10,border:`1px solid ${simulateFeaturedSupplier?"var(--c-heading)":"var(--c-border)"}`,overflow:"hidden"}}>
                <div className="row-between-pad">
                  <div>
                    <div className="t-strong-text">Force-show Featured Supplier card</div>
                    <div className="t-meta">Bypass dismissed-state and month gating — always shows the first featured supplier (UAT helper).</div>
                  </div>
                  <button onClick={()=>onSimulateFeaturedSupplier(prev=>!prev)}
                    style={{background:simulateFeaturedSupplier?"var(--c-heading)":"rgba(45,90,45,0.08)",color:simulateFeaturedSupplier?"#fff":"var(--c-heading)",border:"none",borderRadius:20,padding:"6px 14px",fontSize:'var(--font-small)',fontWeight:600,cursor:'pointer',flexShrink:0,marginLeft:12}}>
                    {simulateFeaturedSupplier?"On":"Off"}
                  </button>
                </div>
              </div>
              {DEV_TOOLS.map(({key,label,desc,danger})=>(
                <div key={key} style={{borderRadius:10,border:`1px solid ${devConfirm===key?(danger?"var(--c-danger-border)":DEV_BLUE):"var(--c-border)"}`,overflow:"hidden"}}>
                  <div className="row-between-pad">
                    <div>
                      <div className="t-strong-text">{label}</div>
                      <div className="t-meta">{desc}</div>
                    </div>
                    <button onClick={()=>setDevConfirm(devConfirm===key?null:key)}
                      style={{background:danger?"var(--c-danger-bg)":"rgba(25,118,210,0.08)",color:danger?"var(--c-danger)":DEV_BLUE,border:"none",borderRadius:20,padding:"6px 14px",fontSize:'var(--font-small)',fontWeight:600,cursor:'pointer',flexShrink:0,marginLeft:12}}>
                      {devConfirm===key?"Cancel":"Run"}
                    </button>
                  </div>
                  {devConfirm===key&&(
                    /* Phase 31.1 Plan 05 / UAT Gap 8 — dropdown background uses theme tokens
                       (was hardcoded #fff8f8 / rgba(25,118,210,0.04) which stayed near-white
                       in OS dark mode, leaving "Are you sure?" text illegible). */
                    <div style={{padding:"10px 14px",background:danger?"var(--c-danger-bg)":"var(--c-surface)",borderTop:`1px solid ${danger?"var(--c-danger-border)":DEV_BLUE}`,display:"flex",alignItems:"center",gap:10}}>
                      <span style={{fontSize:'var(--font-tiny)',color:danger?"var(--c-danger)":DEV_BLUE,flex:1}}>Are you sure? This cannot be undone.</span>
                      <button onClick={()=>runDevAction(key)} style={{background:danger?"var(--c-danger)":DEV_BLUE,color:"#fff",border:"none",borderRadius:20,padding:"6px 16px",fontSize:'var(--font-tiny)',fontWeight:700,cursor:"pointer"}}>Confirm</button>
                    </div>
                  )}
                </div>
              ))}
              {onLaunchAdmin&&(
                <div style={{borderRadius:10,border:`1px solid ${DEV_BLUE}`,overflow:"hidden"}}>
                  <div className="row-between-pad">
                    <div>
                      <div className="t-strong-text">Launch admin panel</div>
                      <div className="t-meta">Opens admin panel without sign-in (sets dev bypass flag)</div>
                    </div>
                    <button
                      onClick={()=>{
                        try{localStorage.setItem('tend:adminDevBypass','true');}catch{}
                        onLaunchAdmin();
                      }}
                      style={{background:`rgba(25,118,210,0.08)`,color:DEV_BLUE,border:"none",borderRadius:20,padding:"6px 14px",fontSize:'var(--font-small)',fontWeight:600,cursor:'pointer',flexShrink:0,marginLeft:12}}>
                      Open
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
        {/* Phase 40 Plan 03 — Account section. Plan 04 will add identity row,
            activity-log row, sign-out / delete-account rows. For Plan 03 we
            ship the sync-status row + dead-letter affordance only. */}
        <div style={{marginTop:24,paddingTop:20,borderTop:"1px solid var(--c-border)"}}>
          <h3 style={{fontFamily:"var(--font-family-display)",fontSize:'var(--font-body)',fontWeight:700,color:"var(--c-heading)",margin:"0 0 12px"}}>Account</h3>
          {!authSession ? (
            <div className="row-between">
              <div style={{display:"flex",alignItems:"center",gap:10,flex:1,minWidth:0}}>
                <Cloud size={16} className="t-heading-shrink"/>
                <div>
                  <div className="t-strong-text">Sign in to back up your garden</div>
                  <div className="t-meta">Free to sign in. Your local garden uploads on first sign-in.</div>
                </div>
              </div>
              <button onClick={onSignIn}
                style={{background:"var(--c-brand-deep)",color:"var(--c-accent-text)",border:"none",borderRadius:20,padding:"8px 16px",fontSize:'var(--font-body)',fontWeight:600,cursor:'pointer',flexShrink:0}}>
                Sign in
              </button>
            </div>
          ) : (
            <>
              {/* Phase 40 Plan 04 — Identity row (provider + signed-in date). */}
              {(() => {
                const email = authSession?.user?.email || '';
                const provider = authSession?.user?.app_metadata?.provider || 'email';
                const created = authSession?.user?.created_at || authSession?.user?.last_sign_in_at || null;
                const sinceLabel = created ? new Date(created).toLocaleDateString('en-GB',{year:'numeric',month:'short',day:'numeric'}) : null;
                const providerLabel = provider.charAt(0).toUpperCase() + provider.slice(1);
                return (
                  <div className="row-between" style={{marginBottom:12}}>
                    <div style={{display:"flex",alignItems:"center",gap:10,flex:1,minWidth:0}}>
                      <User size={16} className="t-heading-shrink"/>
                      <div style={{minWidth:0}}>
                        <div className="t-strong-text" style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:240}}>{email}</div>
                        <div className="t-meta">Signed in via <em>{providerLabel}</em>{sinceLabel?<> · since <em>{sinceLabel}</em></>:null}</div>
                      </div>
                    </div>
                  </div>
                );
              })()}
              {/* Sync status row */}
              <div className="row-between" style={{marginBottom:12}}>
                <div style={{display:"flex",alignItems:"center",gap:10,flex:1,minWidth:0}}>
                  <Cloud size={16} className="t-heading-shrink"/>
                  <div>
                    <div className="t-strong-text">Sync status</div>
                    <div className="t-meta">{deadLetterCount>0?`${deadLetterCount} change${deadLetterCount===1?'':'s'} failed to sync`:`Backed up to the cloud`}</div>
                  </div>
                </div>
                <SyncStatusIndicator
                  status={deadLetterCount>0?'failed':syncStatus.status}
                  count={deadLetterCount>0?deadLetterCount:syncStatus.count}
                  onTap={()=>setShowDeadLetter(true)}
                />
              </div>
              {/* Phase 40 Plan 04 — Activity log link row. */}
              <div className="row-between" style={{marginBottom:12}}>
                <div style={{display:"flex",alignItems:"center",gap:10,flex:1,minWidth:0}}>
                  <ListTodo size={16} className="t-heading-shrink"/>
                  <div>
                    <div className="t-strong-text">Activity log</div>
                    <div className="t-meta">See what's been changed in your garden</div>
                  </div>
                </div>
                <button onClick={()=>setShowActivityLog(true)} aria-label="Open activity log"
                  style={{background:"none",border:"none",cursor:"pointer",color:"var(--c-text-2)",padding:6,borderRadius:6,minHeight:44,display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <ChevronRight size={18}/>
                </button>
              </div>
              {/* Sign-out row */}
              <div className="row-between" style={{marginBottom:12}}>
                <div style={{display:"flex",alignItems:"center",gap:10,flex:1,minWidth:0}}>
                  <LogOut size={16} className="t-heading-shrink"/>
                  <div>
                    <div className="t-strong-text">Sign out</div>
                    <div className="t-meta">You can sign back in anytime to restore your data</div>
                  </div>
                </div>
                <button onClick={()=>setConfirmSignOut(true)}
                  style={{background:"var(--c-btn-bg)",color:"var(--c-btn-text)",border:"none",borderRadius:20,padding:"6px 16px",fontSize:'var(--font-body)',fontWeight:600,cursor:'pointer',flexShrink:0}}>
                  Sign out
                </button>
              </div>
              {/* Phase 40 Plan 04 — Delete account row. */}
              <div className="row-between">
                <div style={{display:"flex",alignItems:"center",gap:10,flex:1,minWidth:0}}>
                  <Trash2 size={16} style={{color:"var(--c-danger)",flexShrink:0}}/>
                  <div>
                    <div className="t-strong-text">Delete account</div>
                    <div className="t-meta">Permanently remove your account, garden, and all data. This can't be undone.</div>
                  </div>
                </div>
                <button onClick={()=>{setDeleteError(null);setShowDeleteAccount(true);}}
                  style={{background:"var(--c-bg)",color:"var(--c-danger)",border:"1px solid var(--c-danger-border)",borderRadius:20,padding:"6px 16px",fontSize:'var(--font-body)',fontWeight:600,cursor:'pointer',flexShrink:0}}>
                  Delete account…
                </button>
              </div>
            </>
          )}
        </div>
        {/* Phase 31.1 Plan 04 — Garden Reset (was "Danger zone" — renamed per UAT Gap 5); always visible, NOT dev-gated. Typed-DELETE confirmation dialog remains the sole safety gate. */}
        <div style={{marginTop:24,paddingTop:20,borderTop:"1px solid var(--c-border)"}}>
          <h3 style={{fontFamily:"var(--font-family-display)",fontSize:'var(--font-body)',fontWeight:700,color:"var(--c-heading)",margin:"0 0 12px"}}>Garden Reset</h3>
          <div className="row-between">
            <div style={{display:"flex",alignItems:"center",gap:10,flex:1,minWidth:0}}>
              <Trash2 size={16} className="t-heading-shrink"/>
              <div>
                <div className="t-strong-text">Delete all data</div>
                <div className="t-meta">Permanently remove all plants, spaces, notes, tasks, and reset onboarding</div>
              </div>
            </div>
            <button onClick={()=>{onDeleteAllData?.();}}
              style={{background:"var(--c-danger-bg)",color:"var(--c-danger)",border:"1px solid var(--c-danger-border)",borderRadius:20,padding:"6px 16px",fontSize:'var(--font-body)',fontWeight:600,cursor:'pointer',flexShrink:0}}>
              Delete
            </button>
          </div>
        </div>
        <div style={{display:"flex",justifyContent:"flex-end",marginTop:20}}>
          <button onClick={onClose} style={{background:"var(--c-border)",color:"var(--c-text-2)",border:"none",borderRadius:20,padding:"9px 18px",fontSize:'var(--font-small)'}}>Close</button>
        </div>
      </div>
      {showDeadLetter && (
        <DeadLetterModal
          ops={readDeadLetter()}
          onTryAgain={handleTryAgain}
          onDiscard={handleDiscardOne}
          onTryAll={handleTryAll}
          onDiscardAll={handleDiscardAll}
          onClose={()=>setShowDeadLetter(false)}
        />
      )}
      {/* Phase 40 Plan 04 Task 1 — Delete account modal */}
      {showDeleteAccount && (
        <DeleteAccountModal
          plantsCount={plants.length}
          inProgress={deleting}
          errorMessage={deleteError}
          onConfirm={async ()=>{
            if (!onDeleteAccount) { setDeleteError("Couldn't delete account."); return; }
            setDeleting(true);
            setDeleteError(null);
            try {
              await onDeleteAccount();
              // Successful path triggers window.location.replace — modal stays open
              // until the redirect completes; no further state work needed here.
            } catch (e) {
              setDeleteError(e?.message ? `Couldn't delete account: ${e.message}` : "Couldn't delete account. Try again, or contact support.");
              setDeleting(false);
            }
          }}
          onCancel={()=>{ if (!deleting) { setShowDeleteAccount(false); setDeleteError(null); } }}
        />
      )}
      {/* Phase 40 Plan 04 Task 1 — Sign-out single-step confirm. */}
      {confirmSignOut && (
        <div role="dialog" aria-modal="true"
          style={{position:"fixed",inset:0,background:"var(--c-overlay)",zIndex:2050,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div onClick={(e)=>e.stopPropagation()} style={{background:"var(--c-bg)",borderRadius:16,padding:24,maxWidth:340,width:"100%",boxShadow:"0 8px 40px rgba(0,0,0,0.2)"}}>
            <h2 style={{fontFamily:"var(--font-family-display)",fontSize:"var(--font-h3)",color:"var(--c-heading)",margin:"0 0 8px",fontWeight:600}}>Sign out of Tend?</h2>
            <p style={{fontSize:"var(--font-body-size)",color:"var(--c-text)",margin:"0 0 16px",lineHeight:1.55}}>You can sign back in anytime to restore your data.</p>
            <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
              <button onClick={()=>setConfirmSignOut(false)} style={{background:"var(--c-btn-bg)",color:"var(--c-btn-text)",border:"none",borderRadius:20,padding:"9px 18px",fontSize:'var(--font-body)',fontWeight:600,cursor:'pointer'}}>Cancel</button>
              <button onClick={()=>{setConfirmSignOut(false); onSignOut?.();}} style={{background:"var(--c-heading)",color:"#fff",border:"none",borderRadius:20,padding:"9px 18px",fontSize:'var(--font-body)',fontWeight:600,cursor:'pointer'}}>Sign out</button>
            </div>
          </div>
        </div>
      )}
      {/* Phase 40 Plan 04 Task 2 — Activity log Settings sub-page (full-screen overlay). */}
      {showActivityLog && (
        <ActivityLogView
          jwt={authSession?.access_token || null}
          currentUserId={authSession?.user?.id || null}
          onBack={()=>setShowActivityLog(false)}
        />
      )}
    </div>
  );
}
